import React, { useEffect, useRef } from "react";
import "./TBM.css";
import { sponsors } from "../data/sponsors";

const designs = [
  { month: "June", src: "/images/june tbm screenshot.png" },
  { month: "July", src: "/images/july tbm screenshot.png" },
  { month: "August", src: "/images/august tbm screenshot.png" },
  { month: "September", src: "/images/September 2025 TBM.png" },
];

// Only sponsors with images appear in the carousel
const carouselSponsors = sponsors.filter((s) => s.src);

const TBM = () => {
  const floatingImageRef = useRef(null);
  const leftFloatingImageRef = useRef(null);
  const centerLeftFloatingImageRef = useRef(null);
  const centerRightFloatingImageRef = useRef(null);

  useEffect(() => {
    let frame = null;

    const update = () => {
      frame = null;
      const images = [
        floatingImageRef.current,
        leftFloatingImageRef.current,
        centerLeftFloatingImageRef.current,
        centerRightFloatingImageRef.current,
      ];
      if (images.some((img) => !img)) return;

      const hideAll = () => images.forEach((img) => (img.style.opacity = 0));

      // The floating design images are a wide-screen effect only
      const windowWidth = window.innerWidth;
      if (windowWidth < 1024) {
        hideAll();
        return;
      }

      const scrollY = window.scrollY;
      const tbmSection = document.querySelector(".tbm-section");
      const tbmTitle = document.querySelector(".tbm-title");
      const designEvolution = document.querySelector(".design-progression");
      const evolutionTitle = document.querySelector(".progression-title");

      if (!tbmSection || !tbmTitle || !designEvolution || !evolutionTitle)
        return;

      const windowHeight = window.innerHeight;
      const titleTop = tbmTitle.offsetTop;
      const titleHeight = tbmTitle.offsetHeight;
      const evolutionTitleTop = evolutionTitle.offsetTop;

      const triggerPoint = evolutionTitleTop + windowHeight * 0.5;

      if (scrollY < triggerPoint) {
        hideAll();
        return;
      }

      const sectionTop = tbmSection.offsetTop;
      const evolutionOffset = designEvolution.offsetTop - sectionTop;

      const animationStart = triggerPoint;
      const animationEnd = triggerPoint + windowHeight * 1.5;
      const sectionProgress = Math.max(
        0,
        Math.min(1, (scrollY - animationStart) / (animationEnd - animationStart))
      );

      const isWideScreen = windowWidth >= 1400;

      const startX = isWideScreen ? -120 : -80;
      const endX = isWideScreen ? -200 : -150;
      const endY = evolutionOffset + windowHeight * 0.5 + 200;

      const zigzagAmplitude = isWideScreen ? 60 : 40;
      const zigzagOffset =
        Math.sin(sectionProgress * Math.PI * 3) * zigzagAmplitude;

      const currentX = startX + (endX - startX) * sectionProgress + zigzagOffset;
      const currentY = endY * sectionProgress;

      let scale = 1;
      if (sectionProgress > 0.75) {
        const growthProgress = (sectionProgress - 0.75) / 0.25;
        scale = 1 + growthProgress * 7;
      }

      const titleScreenPosition = titleTop - scrollY;
      const initialY = Math.max(20, titleScreenPosition + titleHeight / 2);
      const finalY = initialY + currentY;

      floatingImageRef.current.style.right = `${Math.abs(currentX)}px`;
      floatingImageRef.current.style.top = `${finalY - 100}px`;
      floatingImageRef.current.style.transform = `scale(${scale}) translateY(-50%)`;

      leftFloatingImageRef.current.style.left = `${Math.abs(currentX)}px`;
      leftFloatingImageRef.current.style.top = `${finalY - 100}px`;
      leftFloatingImageRef.current.style.transform = `scale(${scale}) translateY(-50%)`;

      centerRightFloatingImageRef.current.style.right = `${Math.abs(currentX) + 350}px`;
      centerRightFloatingImageRef.current.style.top = `${finalY - 75}px`;
      centerRightFloatingImageRef.current.style.transform = `scale(${scale * 0.9}) translateY(-50%)`;

      centerLeftFloatingImageRef.current.style.left = `${Math.abs(currentX) + 350}px`;
      centerLeftFloatingImageRef.current.style.top = `${finalY - 75}px`;
      centerLeftFloatingImageRef.current.style.transform = `scale(${scale * 0.9}) translateY(-50%)`;

      const opacity =
        sectionProgress > 0.9 ? 1 - (sectionProgress - 0.9) * 10 : 0.5;
      floatingImageRef.current.style.opacity = Math.max(0, opacity);
      leftFloatingImageRef.current.style.opacity = Math.max(0, opacity);
      centerRightFloatingImageRef.current.style.opacity = Math.max(0, opacity * 0.8);
      centerLeftFloatingImageRef.current.style.opacity = Math.max(0, opacity * 0.8);
    };

    const handleScroll = () => {
      if (frame === null) frame = requestAnimationFrame(update);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("resize", handleScroll);
    update();
    return () => {
      if (frame !== null) cancelAnimationFrame(frame);
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleScroll);
    };
  }, []);

  return (
    <section className="tbm-section">
      <div className="tbm-container">
        <div className="tbm-header">
          <h2 className="tbm-title">The TBM</h2>
          <img
            ref={floatingImageRef}
            src="/images/September 2025 TBM.png"
            alt=""
            className="tbm-floating-image"
          />
          <img
            ref={leftFloatingImageRef}
            src="/images/june tbm screenshot.png"
            alt=""
            className="tbm-floating-image-left"
          />
          <img
            ref={centerRightFloatingImageRef}
            src="/images/august tbm screenshot.png"
            alt=""
            className="tbm-floating-image-center-right"
          />
          <img
            ref={centerLeftFloatingImageRef}
            src="/images/july tbm screenshot.png"
            alt=""
            className="tbm-floating-image-center-left"
          />
        </div>

        {/* NDA (Exhibit A, "Team Information"): details of our technical approach may
            only be released publicly with the team leader's permission. Get sign-off
            before adding to or changing this block. */}
        <div className="machine-2027">
          <figure className="machine-2027-figure">
            <img
              src="/images/tbm-2027/microtbm-iso.png"
              alt="Line drawing of the 2026–27 microTBM shield, viewed from the front"
              className="machine-2027-image"
              width="1200"
              height="811"
              loading="lazy"
            />
          </figure>
          <div className="machine-2027-text">
            <p className="machine-2027-eyebrow">2026–27 Season</p>
            <h3 className="machine-2027-title">A Slurry microTBM</h3>
            <p>
              Last year, our team of four built a vertical tunneling machine.
              This year, our team of ten is going HORIZONTAL.
            </p>
            <p>
              Rotating cutterhead at the front of our steel shield. Hydraulic
              cylinders thrust the machine forward, and we pipe jack to go
              further than one stroke length of the cylinder rods.
            </p>
            <p>
              Slurry pumped to the face carries the cuttings back out through the
              casing, so the tunnel is lined as it is dug.
            </p>
            <ul className="machine-2027-specs">
              <li>
                <span className="spec-value">630 mm</span>
                <span className="spec-label">bore diameter</span>
              </li>
              <li>
                <span className="spec-value">Slurry</span>
                <span className="spec-label">spoil removal</span>
              </li>
              <li>
                <span className="spec-value">Hydraulic</span>
                <span className="spec-label">pipe jacking</span>
              </li>
              <li>
                <span className="spec-value">Steel</span>
                <span className="spec-label">casing lining</span>
              </li>
            </ul>
          </div>
        </div>

        <div className="design-progression">
          <h3 className="progression-title">2025–26 Season: Design Evolution</h3>
          <div className="design-timeline">
            {designs.map((design) => (
              <figure key={design.month} className="design-item">
                <img
                  src={design.src}
                  alt={`${design.month} TBM design`}
                  className="design-image"
                  loading="lazy"
                />
                <figcaption className="design-label">{design.month}</figcaption>
              </figure>
            ))}
          </div>
        </div>

        {/* Sponsor carousel — auto-generated from data/sponsors.js.
            The list is rendered twice and the track slides by exactly half its width,
            so the loop is seamless no matter how many sponsors there are. */}
        <div className="sponsor-carousel">
          <div className="carousel-track">
            {[...carouselSponsors, ...carouselSponsors].map((sponsor, index) => {
              const isDuplicate = index >= carouselSponsors.length;
              return (
                <a
                  key={index}
                  href={sponsor.link || undefined}
                  target={sponsor.link ? "_blank" : undefined}
                  rel="noopener noreferrer"
                  className="carousel-logo-link"
                  aria-hidden={isDuplicate || undefined}
                  tabIndex={isDuplicate ? -1 : undefined}
                >
                  <img
                    src={sponsor.src}
                    alt={sponsor.name}
                    className="carousel-logo"
                    loading="lazy"
                  />
                </a>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default TBM;
